import React, { useEffect } from 'react';

import { NoraTextAreaInput } from 'ethos-design-system';

import { Box, Button, Divider, Drawer, Typography } from '@mui/material';

import {
  FlexRow,
  NoraButton,
  Select,
  Spacer,
} from '../../../../common/components/v2';
import { Resource } from '../../../../common/constants/resource';
import ElementLocators from '../../../../common/ElementLocators';
import { RequiredFieldErrorBar } from '../RequiredFieldErrorBar';
import { RequiredFieldTitle } from '../RequiredFieldTitle';
import styles from '../styles.module.scss';

export enum Reasons {
  OnNoticeAtTimeOfApplication = 'On notice at time of application',
  ExistingPolicyRatingCoversRisk = 'Existing policy rating covers risk',
  GuidelinesNotFollowed = 'Guidelines not followed',
  EvidenceOfDiagnosisOrTreatmentAfterApplication = 'Evidence of diagnosis or treatment is after application date',
  NoEvidenceApplicantWasAwareOfResultsOrSuspectedDiagnosis = 'No evidence the applicant was aware of results or suspected diagnosis',
  RecordsNotRecent = 'Records not recent',
  ApplicationDidNotContemplateReasonForRecommendedRescission = 'Application did not contemplate reason for recommended rescission',
  ApplicantDisclosedConditionOnApplication = 'Applicant disclosed condition on application',
  Other = 'Other',
}

interface IEditDisagreeToRescindProps {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  selectedReason: string | null;
  setSelectedReason: (val: string) => void;
  description: string;
  setDescription: (val: string) => void;
  showError: boolean;
  setShowError: (val: boolean) => void;
  showSummary: (val: boolean) => void;
  onReviewClick: () => void;
}

export default function EditDisagreeToRescind(
  props: IEditDisagreeToRescindProps,
) {
  useEffect(() => {
    props.setShowError(false);
  }, [props.isOpen]);

  return (
    <Drawer
      open={props.isOpen}
      anchor="right"
      variant="persistent"
      classes={{ paper: styles.Drawer }}
      transitionDuration={{ enter: 0, exit: 0 }}
    >
      <Box mt={6}>
        <Box
          m={3}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1">
            {Resource.DISAGREE_TO_RESCIND}
          </Typography>
          <Button onClick={() => props.setIsOpen(false)}>
            <Typography variant="subtitle2" color="textSecondary">
              {Resource.CANCEL}
            </Typography>
          </Button>
        </Box>
        <Divider />
        <Box m={3}>
          {props.showError && (
            <>
              <RequiredFieldErrorBar />
              <Spacer height={8} />
            </>
          )}
          <Spacer height={16} />
          <FlexRow className={styles.DrawerSubTitle}>
            <RequiredFieldTitle title={Resource.REASON} />
          </FlexRow>
          <FlexRow>
            <Select
              className={styles.Dropdown}
              placeholder={Resource.SELECT_PLACEHOLDER}
              value={
                props.selectedReason && {
                  value: props.selectedReason,
                  label: props.selectedReason,
                }
              }
              onChange={({ value }) => props.setSelectedReason(value)}
              options={Object.values(Reasons).map(value => ({
                value,
                label: value,
              }))}
            />
          </FlexRow>
          <Spacer height={16} />
          <Spacer height={16} />
          <FlexRow className={styles.DrawerSubTitle}>
            {Reasons.Other === props.selectedReason ? (
              <RequiredFieldTitle title={Resource.DESCRIPTION} />
            ) : (
              Resource.DESCRIPTION
            )}
          </FlexRow>
          <FlexRow>
            <NoraTextAreaInput
              name={
                ElementLocators.LOCATOR_DASHBOARD_DISAGREE_TO_RESCIND_DESCRIPTION
              }
              data-tid={
                ElementLocators.LOCATOR_DASHBOARD_DISAGREE_TO_RESCIND_TEXTAREA
              }
              placeholder="Describe the reason"
              textClassName={styles.TextareaInput}
              onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>) => {
                props.setDescription(ev.currentTarget.value);
              }}
              value={props.description}
            />
          </FlexRow>
        </Box>
      </Box>
      <Box>
        <NoraButton
          name={ElementLocators.LOCATOR_REVIEW_APPROVAL_BUTTON}
          className={styles.DrawerButton}
          onClick={props.onReviewClick}
        >
          {Resource.REVIEW_DECISION}
        </NoraButton>
      </Box>
    </Drawer>
  );
}
